<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import { usePlanStore } from "@/stores/usePlanStore";
import { useSubscriptionStore } from "@/stores/useSubscriptionStore";
import { useKeyboardDropdown } from "@/composables/useKeyboardDropdown";

// ─────────────────────────────────────────────
// PROPS / EMITS
// ─────────────────────────────────────────────
const props = defineProps({
  show: { type: Boolean, default: false },
  userId: { type: Number, required: true },
  userName: { type: String, default: "" }, // nome exibido no subtitle do header
});

const emit = defineEmits(["close", "refresh"]);

const planStore = usePlanStore();
const subscriptionStore = useSubscriptionStore();

// ─────────────────────────────────────────────
// ESTADO DO FORMULÁRIO
// ─────────────────────────────────────────────
const form = ref({
  planId: null,
  durationMonths: null, // null = perpétuo
});

const errors = ref({});
const isSubmitting = ref(false);

// ─────────────────────────────────────────────
// AUTOCOMPLETE — PLANO
// ─────────────────────────────────────────────
const planSearch = ref("");
const planResults = ref([]);
const planLoading = ref(false);
const showPlanDropdown = ref(false);
const selectedPlanLabel = ref("");
let planDebounce = null;

const planDropdownRef = ref(null);

const {
  activeIndex: activePlanIndex,
  resetActiveIndex: resetActivePlanIndex,
  handleKeydown: handlePlanKeydown,
  scrollActiveItemIntoView: scrollActivePlanIntoView,
} = useKeyboardDropdown({
  getItems: () => planResults.value,
  isOpen: () => showPlanDropdown.value,
  open: () => {
    showPlanDropdown.value = planResults.value.length > 0;
  },
  close: () => {
    showPlanDropdown.value = false;
  },
  onSelect: selectPlan,
});

function isOneTimePlan(plan) {
  return plan?.billingCycle === "OneTime";
}

async function onPlanInput() {

  resetActivePlanIndex();

  form.value.planId = null;
  selectedPlanLabel.value = "";

  const q = planSearch.value.trim();

  if (!q) {
    planResults.value = [];
    showPlanDropdown.value = false;
    return;
  }

  planLoading.value = true;
  clearTimeout(planDebounce);

  planDebounce = setTimeout(async () => {
    try {
      await planStore.fetchPlansPaged(1, 10, q);

      // ✅ CORTESIA: exibe apenas planos avulsos.
      // Como a duração vem de durationMonths, planos mensais/anuais
      // não precisam aparecer neste modal.
      planResults.value = (planStore.plans || []).filter(isOneTimePlan);

      showPlanDropdown.value = planResults.value.length > 0;
    } catch {
      planResults.value = [];
      showPlanDropdown.value = false;
    } finally {
      planLoading.value = false;
    }
  }, 3000);
}

function selectPlan(plan) {
  form.value.planId = plan.planId;

  // ✅ CORTESIA: como o modal só permite planos avulsos,
  // o label não precisa exibir o ciclo.
  selectedPlanLabel.value = plan.name || `#${plan.planId}`;

  planSearch.value = selectedPlanLabel.value;
  showPlanDropdown.value = false;
  planResults.value = [];

  resetActivePlanIndex();
}

function clearPlan() {
  form.value.planId = null;
  planSearch.value = "";
  selectedPlanLabel.value = "";
  planResults.value = [];
  showPlanDropdown.value = false;
  resetActivePlanIndex();

}

// ─────────────────────────────────────────────
// DURAÇÃO — chips de preset + campo livre
// ─────────────────────────────────────────────
const durationMode = ref("preset"); // "preset" | "custom"
const presetValue = ref(1); // null = perpétuo
const customMonths = ref("");

const durationLabel = computed(() => {
  if (durationMode.value === "custom") {
    const n = parseInt(customMonths.value);
    return n > 0 ? `${n} mês${n > 1 ? "es" : ""}` : "—";
  }
  if (presetValue.value === null) return "Perpétuo";
  return `${presetValue.value} mês${presetValue.value > 1 ? "es" : ""}`;
});

// Sincroniza form.durationMonths
watch(
  [durationMode, presetValue, customMonths],
  () => {
    if (durationMode.value === "preset") {
      form.value.durationMonths = presetValue.value;
    } else {
      const n = parseInt(customMonths.value);
      form.value.durationMonths = n > 0 ? n : null;
    }
  },
  { immediate: true }
);

function selectPreset(val) {
  presetValue.value = val;
  durationMode.value = "preset";
}

// ─────────────────────────────────────────────
// VALIDAÇÃO
// ─────────────────────────────────────────────
function validate() {
  const e = {};
  if (!form.value.planId) e.planId = "Selecione um plano.";
  if (durationMode.value === "custom") {
    const n = parseInt(customMonths.value);
    if (!customMonths.value || n <= 0)
      e.durationMonths = "Informe um número de meses válido (≥ 1).";
    else if (n > 120) e.durationMonths = "Máximo de 120 meses (10 anos).";
  }
  errors.value = e;
  return Object.keys(e).length === 0;
}

// ─────────────────────────────────────────────
// SUBMIT
// ─────────────────────────────────────────────
async function handleSubmit() {
  if (!validate()) return;
  isSubmitting.value = true;
  try {
    const payload = {
      userId: props.userId,
      planId: form.value.planId,
      ...(form.value.durationMonths != null
        ? { durationMonths: form.value.durationMonths }
        : {}),
    };
    await subscriptionStore.grantCourtesy(payload);
    triggerToast("Acesso cortesia concedido com sucesso!", "success");
    emit("refresh");
    setTimeout(() => closeModal(), 1400);
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.message ||
      "Erro ao conceder cortesia.";
    triggerToast(msg, "error");
  } finally {
    isSubmitting.value = false;
  }
}

// ─────────────────────────────────────────────
// RESET ao abrir
// ─────────────────────────────────────────────
watch(
  () => props.show,
  (val) => {
    if (val) resetForm();
  }
);

function resetForm() {
  form.value = { planId: null, durationMonths: null };
  errors.value = {};
  planSearch.value = "";
  selectedPlanLabel.value = "";
  planResults.value = [];
  showPlanDropdown.value = false;
  durationMode.value = "preset";
  presetValue.value = 1;
  customMonths.value = "";
}

// ─────────────────────────────────────────────
// FECHAR DROPDOWN AO CLICAR FORA
// ─────────────────────────────────────────────
function handleOutsideClick(e) {
  if (!e.target.closest(".autocomplete-wrap")) {
    showPlanDropdown.value = false;
  }
}
onMounted(() => {
  document.addEventListener("mousedown", handleOutsideClick);
  resetForm();
});
onBeforeUnmount(() => {
  document.removeEventListener("mousedown", handleOutsideClick);
});

// ─────────────────────────────────────────────
// FECHAR MODAL
// ─────────────────────────────────────────────
function closeModal() {
  if (isSubmitting.value) return;
  emit("close");
}

// ─────────────────────────────────────────────
// TOAST
// ─────────────────────────────────────────────
const showToast = ref(false);
const toastMsg = ref("");
const toastType = ref("success");
let toastTimer = null;

function triggerToast(msg, type = "success") {
  toastMsg.value = msg;
  toastType.value = type;
  showToast.value = true;
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => (showToast.value = false), 4000);
}

watch(activePlanIndex, () => {
  scrollActivePlanIntoView(planDropdownRef);
});

</script>

<template>
  <Transition name="modal-fade">
    <div
      v-if="show"
      class="modal-backdrop"
      @keydown.esc="closeModal"
      tabindex="-1"
    >
      <div class="modal-panel" role="dialog" aria-modal="true">
        <form @submit.prevent="handleSubmit" novalidate>
          <!-- ── HEADER ── -->
          <div class="modal-header">
            <div class="header-left">
              <svg class="header-icon" viewBox="0 0 20 20" fill="currentColor">
                <path
                  d="M10 2a1 1 0 00-1 1v1H6a2 2 0 00-2 2v1H3a1 1 0 000 2h1v7a2 2 0 002 2h8a2 2 0 002-2V9h1a1 1 0 100-2h-1V6a2 2 0 00-2-2h-3V3a1 1 0 00-1-1zM7 6h6v1H7V6zM5 9h10v7H5V9z"
                />
              </svg>
              <div>
                <h3 class="modal-title">Conceder Cortesia</h3>
                <p v-if="userName" class="modal-subtitle">{{ userName }}</p>
              </div>
            </div>
            <button
              type="button"
              class="modal-close-btn"
              :disabled="isSubmitting"
              @click="closeModal"
              aria-label="Fechar"
            >
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fill-rule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clip-rule="evenodd"
                />
              </svg>
            </button>
          </div>

          <!-- ── BODY ── -->
          <div class="modal-body">
            <!-- PLANO (autocomplete) -->
            <div class="form-group autocomplete-wrap">
              <label class="form-label">
                Plano <span class="required-star">*</span>
              </label>
              <div class="autocomplete-input-wrap">
                <input
                  type="text"
                  v-model="planSearch"
                  @input="onPlanInput"
                  @keydown="handlePlanKeydown"
                  @focus="showPlanDropdown = planResults.length > 0"
                  class="input-base"
                  :class="{
 'is-invalid': errors.planId,
                    'is-selected': !!form.planId,
                  }"
                  placeholder="Buscar plano…"
                  autocomplete="off"
                />
                <button
                  v-if="form.planId"
                  type="button"
                  class="clear-btn"
                  @click="clearPlan"
                >
                  <svg viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fill-rule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </button>
                <span v-if="planLoading" class="spinner-inline" />
              </div>
              <ul
                v-if="showPlanDropdown"
                ref="planDropdownRef"
                class="dropdown-list"
              >
                <li
                  v-for="(p, index) in planResults"
                  :key="p.planId"
                  @mousedown.prevent="selectPlan(p)"
                  class="dropdown-item"
                  :class="{
 'dropdown-item--active': index === activePlanIndex,
                  }"
                  :data-active="index === activePlanIndex"
                >
                  <span class="dropdown-main">
                    {{ p.name }}
                  </span>
                  <span class="dropdown-sub">#{{ p.planId }}</span>
                </li>
              </ul>
              <span v-if="errors.planId" class="error-msg">{{
                errors.planId
              }}</span>
            </div>

            <!-- DURAÇÃO -->
            <div class="form-group" style="margin-top: 1.1rem">
              <label class="form-label">
                Duração
                <span class="optional-tag">padrão: 1 mês</span>
              </label>

              <!-- Chips de preset -->
              <div class="duration-chips">
                <button
                  v-for="opt in [
                    { label: '1 mês', val: 1 },
                    { label: '3 meses', val: 3 },
                    { label: '6 meses', val: 6 },
                    { label: '1 ano', val: 12 },
                    { label: 'Perpétuo', val: null },
                  ]"
                  :key="String(opt.val)"
                  type="button"
                  class="duration-chip"
                  :class="{
 'duration-chip--active':
                      durationMode === 'preset' && presetValue === opt.val,
                  }"
                  @click="selectPreset(opt.val)"
                >
                  {{ opt.label }}
                </button>

                <button
                  type="button"
                  class="duration-chip duration-chip--custom"
                  :class="{
 'duration-chip--active': durationMode === 'custom',
                  }"
                  @click="
                    durationMode = 'custom';
                    customMonths = '';
                  "
                >
                  Personalizado…
                </button>
              </div>

              <!-- Input numérico livre -->
              <div
                v-if="durationMode === 'custom'"
                class="custom-months-row"
                style="margin-top: 0.5rem"
              >
                <input
                  type="number"
                  v-model="customMonths"
                  min="1"
                  max="120"
                  class="input-base"
                  :class="{ 'is-invalid': errors.durationMonths }"
                  placeholder="Nº de meses (1–120)"
                  style="max-width: 200px"
                />
                <span class="hint-msg" style="align-self: center">meses</span>
              </div>
              <span v-if="errors.durationMonths" class="error-msg">{{
                errors.durationMonths
              }}</span>
              <span class="hint-msg" style="margin-top: 0.25rem">
                Perpétuo = sem data de expiração.
              </span>
            </div>

            <!-- Banner de confirmação (animado) -->
            <Transition name="banner-slide">
              <div
                v-if="form.planId"
                class="confirm-banner"
                style="margin-top: 1.1rem"
              >
                <svg
                  class="w-4 h-4 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>
                  <strong>{{ selectedPlanLabel }}</strong>
                  será concedido por
                  <strong>{{ durationLabel }}</strong>
                  sem passar pelo Mercado Pago.
                </span>
              </div>
            </Transition>
          </div>

          <!-- ── FOOTER ── -->
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              :disabled="isSubmitting"
              @click="closeModal"
            >
              Cancelar
            </button>
            <button
              type="submit"
              class="btn btn-courtesy"
              :disabled="isSubmitting"
            >
              <svg
                v-if="isSubmitting"
                class="w-5 h-5 mr-2 animate-spin"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                />
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                />
              </svg>
              <svg
                v-else
                class="w-5 h-5 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  d="M10 2a1 1 0 00-1 1v1H6a2 2 0 00-2 2v1H3a1 1 0 000 2h1v7a2 2 0 002 2h8a2 2 0 002-2V9h1a1 1 0 100-2h-1V6a2 2 0 00-2-2h-3V3a1 1 0 00-1-1zM7 6h6v1H7V6zM5 9h10v7H5V9z"
                />
              </svg>
              <span>{{
                isSubmitting ? "Concedendo…" : "Conceder Cortesia"
              }}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </Transition>

  <!-- ── TOAST ── -->
  <Teleport to="body">
    <Transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMsg }}</p>
          </div>
          <button type="button" class="toast-close" @click="showToast = false">
            &times;
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* ── TRANSIÇÕES ── */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
.banner-slide-enter-active,
.banner-slide-leave-active {
  transition: all 0.25s ease;
}
.banner-slide-enter-from,
.banner-slide-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.modal-panel {
  max-width: 30rem;
  max-height: calc(100vh - 40px);
}
.modal-panel > form {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.modal-header {
  flex-shrink: 0;
}
.header-left {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}
.header-icon {
  width: 1.25rem;
  height: 1.25rem;
  color: #7c3aed;
  flex-shrink: 0;
}

.modal-subtitle {
  font-size: 0.78rem;
  color: var(--text-muted);
  margin: 0;
  line-height: 1.2;
}

.modal-footer {
  flex-shrink: 0;
}

.required-star {
  color: var(--danger);
  font-weight: 700;
}
.optional-tag {
  font-size: 0.72rem;
  font-weight: 500;
  color: var(--text-muted);
  background-color: var(--surface-3);
  border: 1px solid var(--neutral-200);
  border-radius: 4px;
  padding: 0.05rem 0.4rem;
}

.input-base.is-selected {
  border-color: #86efac;
  background-color: #f0fdf4;
}

/* ── AUTOCOMPLETE ── */
.autocomplete-wrap {
  position: relative;
}
.autocomplete-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}
.autocomplete-input-wrap .input-base {
  padding-right: 2rem;
}

.clear-btn {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.1rem;
  color: var(--text-subtle);
  display: flex;
  align-items: center;
}
.clear-btn:hover {
  color: var(--danger);
}
.clear-btn svg {
  width: 0.9rem;
  height: 0.9rem;
}

.spinner-inline {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  width: 0.85rem;
  height: 0.85rem;
  border: 2px solid var(--neutral-200);
  border-top-color: var(--brand);
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}
@keyframes spin {
  to {
    transform: translateY(-50%) rotate(360deg);
  }
}

.dropdown-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 100;
  background: var(--surface);
  border: 1px solid var(--neutral-200);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  margin-top: 2px;
  max-height: 180px;
  overflow-y: auto;
  padding: 0.25rem 0;
  list-style: none;
}

.dropdown-main {
  font-size: 0.875rem;
  color: var(--text-dark);
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}
.billing-badge {
  font-size: 0.68rem;
  font-weight: 600;
  color: #7c3aed;
  background-color: var(--brand-soft);
  border-radius: 4px;
  padding: 0.05rem 0.35rem;
  letter-spacing: 0.02em;
}
.dropdown-sub {
  font-size: 0.75rem;
  color: var(--text-subtle);
}

/* ── CHIPS DE DURAÇÃO ── */
.duration-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.35rem;
}
.duration-chip {
  padding: 0.3rem 0.75rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 500;
  border: 1px solid var(--neutral-200);
  background-color: var(--surface-2);
  color: var(--text-light);
  cursor: pointer;
  transition: all 0.15s;
}
.duration-chip:hover {
  background-color: var(--brand-soft);
  border-color: #c4b5fd;
  color: var(--text-on-brand-soft);
}
.duration-chip--active {
  background-color: var(--brand-soft);
  border-color: #a78bfa;
  color: var(--text-on-brand-soft);
  font-weight: 600;
  box-shadow: 0 0 0 2px rgba(167, 139, 250, 0.25);
}
.duration-chip--custom {
  border-style: dashed;
}

.custom-months-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* ── CONFIRM BANNER ── */
.confirm-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  background-color: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  font-size: 0.8rem;
  color: var(--success-text);
}

/* ── MENSAGENS ── */
.error-msg {
  color: var(--danger);
  font-size: 0.78rem;
}
.hint-msg {
  color: var(--text-subtle);
  font-size: 0.75rem;
  margin-top: 0.1rem;
}

.btn-courtesy {
  background-color: var(--brand);
  color: var(--text-on-brand);
  font-weight: 600;
}
.btn-courtesy:hover:not(:disabled) {
  background-color: var(--brand-hover);
}

.dropdown-item--active {
  background-color: var(--brand-soft);
}

.dropdown-item--active .dropdown-main {
  color: var(--text-on-brand-soft);
  font-weight: 600;
}
</style>